import { useEffect, useState, type ReactNode } from 'react';
import { extractErrorMessage } from '../api';
import { useUser } from '../providers/UserProvider';
import { clearReaderAction, hasReaderAction, resumeReaderAction } from '../utils/readerPendingAction';
import { AuthLayout } from './AuthLayout';
import {
  applyLocalReaderPreferences,
  discardLocalReaderPreferences,
  hasLocalReaderPreferences,
  localPreferencesAccountId,
} from '../utils/localReaderPreferences';

/** One post-authentication trigger. Reader requests start after acknowledgement,
 * so their first catalog and feed responses already include the imported choices. */
export function LocalReaderPreferences({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useUser();
  const userId = isLoggedIn ? localPreferencesAccountId() : null;
  return userId
    ? <ApplyPreferences key={userId} userId={userId}>{children}</ApplyPreferences>
    : children;
}

function ApplyPreferences({ userId, children }: { userId: string; children: ReactNode }) {
  const [status, setStatus] = useState<'applying' | 'done' | Error>(() => (hasLocalReaderPreferences(userId) || hasReaderAction(userId)) ? 'applying' : 'done');
  useEffect(() => {
    if (status !== 'applying') return;
    let disposed = false;
    void applyLocalReaderPreferences(userId).then(() => resumeReaderAction())
      .then(() => { if (!disposed) setStatus('done'); })
      .catch(error => { if (!disposed) setStatus(new Error(extractErrorMessage(error))); });
    return () => { disposed = true; };
  }, [userId, status]);

  if (status === 'done') return children;
  return <AuthLayout>
    {status === 'applying' ? <p role="status">Saving your news preferences…</p> : <>
      <p role="alert">Your account is signed in, but your news preferences could not be saved: {status.message}</p>
      <button type="button" onClick={() => setStatus('applying')}>Retry</button>
      <button type="button" onClick={() => { discardLocalReaderPreferences(userId); if (hasReaderAction(userId)) clearReaderAction(); setStatus('done'); }}>Discard local preferences</button>
    </>}
  </AuthLayout>;
}
